// Voucher V2 — themes, occasions, aesthetics and sample voucher data.
//
// Mirrors the structure of the design package so VoucherCard can read
// theme, occasion and aesthetic independently. Each axis is decoupled:
//   - THEME drives palette (bg / ink / accent)
//   - AESTHETIC drives composition (editorial / abstract / custom)
//   - OCCASION drives the abstract scene preset + label + tagline
//
// Sample data is hardcoded for now — backend integration is a CTO follow-up.

export const THEMES = [
  { id: 'noir',      name: 'Noir',      bg: '#1A1A1E', ink: 'light', accent: '#E8A838', sub: '#3A3A3E', hero: 'serif' },
  { id: 'charcoal',  name: 'Charcoal',  bg: '#29282D', ink: 'light', accent: '#F5DABF', sub: '#49484D', hero: 'serif' },
  { id: 'cream',     name: 'Cream',     bg: '#F3EADD', ink: 'dark',  accent: '#B34A2C', sub: '#D6C8B4', hero: 'serif' },
  { id: 'bone',      name: 'Bone',      bg: '#EDEAE3', ink: 'dark',  accent: '#29282D', sub: '#D4CFC4', hero: 'serif' },
  { id: 'rose',      name: 'Rose',      bg: '#E8B4C8', ink: 'dark',  accent: '#7A2847', sub: '#C98DA6', hero: 'serif' },
  { id: 'coral',     name: 'Coral',     bg: '#F0654B', ink: 'light', accent: '#F3EADD', sub: '#C9421F', hero: 'sans' },
  { id: 'sunset',    name: 'Sunset',    bg: '#EC8C55', ink: 'dark',  accent: '#2B1B0E', sub: '#C7693B', hero: 'serif' },
  { id: 'amber',     name: 'Amber',     bg: '#E8A838', ink: 'dark',  accent: '#3E2B0A', sub: '#C48A1E', hero: 'sans' },
  { id: 'ocean',     name: 'Ocean',     bg: '#0D3B4F', ink: 'light', accent: '#8DD3E4', sub: '#1B5068', hero: 'serif' },
  { id: 'teal',      name: 'Teal',      bg: '#007EA0', ink: 'light', accent: '#C8EEEE', sub: '#005F7A', hero: 'sans' },
  { id: 'sage',      name: 'Sage',      bg: '#8DB580', ink: 'dark',  accent: '#2C3E26', sub: '#6F9565', hero: 'serif' },
  { id: 'olive',     name: 'Olive',     bg: '#4A5033', ink: 'light', accent: '#D6C97A', sub: '#2F3421', hero: 'serif' },
  { id: 'lavender',  name: 'Lavender',  bg: '#D6D5EC', ink: 'dark',  accent: '#4A3D7A', sub: '#B5B3D3', hero: 'serif' },
  { id: 'plum',      name: 'Plum',      bg: '#3C1F3A', ink: 'light', accent: '#E8B4C8', sub: '#5A3558', hero: 'serif' },
  { id: 'midnight',  name: 'Midnight',  bg: '#1E1B2E', ink: 'light', accent: '#E8A838', sub: '#363249', hero: 'serif' },
  { id: 'pistachio', name: 'Pistachio', bg: '#C8DDB0', ink: 'dark',  accent: '#2E3F1A', sub: '#A9C28D', hero: 'sans' },
];

// 12 occasions the client can pick at gift time. Salon enables/disables
// them in the Designs page (phase 2).
//
// Two separate display-label fields so each aesthetic gets a label that
// describes what the card actually shows, not the occasion it's tied to
// internally:
//   - `subject` — names what's *in* the editorial illustration (Cake,
//      Hearts, Rings). Used by the Editorial tab.
//   - `paletteLabel` — palette-led name for the abstract pattern
//      (Sunrise, Lilac, Holly). Used by the Abstract tab. Abstract
//      cards are pure pattern, so subject names like "Cake" make no
//      sense; palette names ("Sunrise" for warm coral waves) do.
// `name` is kept for places that genuinely need the occasion concept
// (gifting-flow occasion picker, taglines).
export const OCCASIONS = [
  { id: 'birthday',    name: 'Birthday',     subject: 'Cake',      paletteLabel: 'Sunrise',  tagline: 'Happy birthday' },
  { id: 'mothersday',  name: 'Mother’s Day', subject: 'Bloom',     paletteLabel: 'Lilac',    tagline: 'For mum' },
  { id: 'thankyou',    name: 'Thank You',    subject: 'Letter',    paletteLabel: 'Meadow',   tagline: 'With gratitude' },
  { id: 'christmas',   name: 'Christmas',    subject: 'Snowman',   paletteLabel: 'Holly',    tagline: 'Merry Christmas' },
  { id: 'valentines',  name: 'Valentine’s', subject: 'Hearts',    paletteLabel: 'Crush',    tagline: 'With love' },
  { id: 'newyear',     name: 'New Year',     subject: 'Fireworks', paletteLabel: 'Midnight', tagline: 'Here’s to new' },
  { id: 'anniversary', name: 'Anniversary',  subject: 'Rings',     paletteLabel: 'Garnet',   tagline: 'To many more' },
  { id: 'wedding',     name: 'Wedding',      subject: 'Bouquet',   paletteLabel: 'Pearl',    tagline: 'Celebrate love' },
  { id: 'justbecause', name: 'Just Because', subject: 'Sun',       paletteLabel: 'Spectrum', tagline: 'Thinking of you' },
  { id: 'halloween',   name: 'Halloween',    subject: 'Pumpkin',   paletteLabel: 'Ember',    tagline: 'Eat, drink, be scary' },
  { id: 'blossom',     name: 'Spring',       subject: 'Blossom',   paletteLabel: 'Petal',    tagline: 'First bloom' },
  { id: 'autumn',      name: 'Autumn',       subject: 'Leaves',    paletteLabel: 'Amber',    tagline: 'Falling into it' },
];

// Legacy template map — VoucherCard reads tpl.name + tpl.tagline + tpl.glyph
// + tpl.suggestedTheme. Kept separate from OCCASIONS because the design
// uses these for the editorial/minimal aesthetics' chrome (chip + tagline).
export const TEMPLATES = [
  { id: 'birthday',    name: 'Birthday',    glyph: '⁕', suggestedTheme: 'rose',     tagline: 'Happy birthday' },
  { id: 'mothersday',  name: 'Mother’s Day', glyph: '✿', suggestedTheme: 'lavender', tagline: 'For mum' },
  { id: 'thankyou',    name: 'Thank You',   glyph: '❦', suggestedTheme: 'sage',     tagline: 'With gratitude' },
  { id: 'christmas',   name: 'Christmas',   glyph: '✻', suggestedTheme: 'midnight', tagline: 'Season’s greetings' },
  { id: 'valentines',  name: 'Valentine’s', glyph: '♥', suggestedTheme: 'rose',     tagline: 'With love' },
  { id: 'newyear',     name: 'New Year',    glyph: '✺', suggestedTheme: 'noir',     tagline: 'New beginnings' },
  { id: 'anniversary', name: 'Anniversary', glyph: '◆', suggestedTheme: 'plum',     tagline: 'To many more' },
  { id: 'wedding',     name: 'Wedding',     glyph: '✦', suggestedTheme: 'bone',     tagline: 'Celebrate love' },
  { id: 'justbecause', name: 'Just Because', glyph: '◉', suggestedTheme: 'sunset',  tagline: 'Thinking of you' },
  { id: 'halloween',   name: 'Halloween',   glyph: '❁', suggestedTheme: 'amber',    tagline: 'Eat, drink, be scary' },
  { id: 'blossom',     name: 'Spring',      glyph: '✿', suggestedTheme: 'sage',     tagline: 'First bloom' },
  { id: 'autumn',      name: 'Autumn',      glyph: '✹', suggestedTheme: 'sunset',   tagline: 'Falling into it' },
  { id: 'signature',   name: 'Signature',   glyph: '●', suggestedTheme: 'charcoal', tagline: '' },
];

// Three library tiers + Custom. Minimal was dropped — it was one
// composition × 12 colours (functionally one design) and was redundant
// with Custom's solid-colour bg path. Custom is becoming the
// general-purpose designer (gift cards + loyalty + QR booking), so any
// "plain coloured card" need is now better served there.
export const AESTHETICS = [
  { id: 'editorial', name: 'Editorial', desc: 'Illustrated scene per occasion' },
  { id: 'abstract',  name: 'Abstract',  desc: 'Vibrant pattern per occasion' },
  { id: 'custom',    name: 'Custom',    desc: 'Salon-branded design with logo' },
];

// SAMPLE_VOUCHERS — `designIds: []` for the per-voucher design picker.
// Empty array = "every available library design applies" (default for new
// vouchers). Populated array = curated subset chosen by the salon in the
// voucher editor (phase 2). Bronze / Silver / Gold use case: Gold sets a
// curated subset of premium designs, Bronze leaves it empty so every
// available design shows.
//
// Two sample vouchers carry curated subsets so the design slide-out's
// "Used by" cross-reference varies meaningfully across designs.
export const SAMPLE_VOUCHERS = [
  { id: 'v1', name: 'Gift Card',         template: 'signature', theme: 'rose',     denominations: [25, 50, 100, 150], value: 50,  promo: null, online: true,  pos: true,  sold: 124, status: 'active', expiry: '12 months', customAmount: true,  customMin: 10, customMax: 500, designIds: [] },
  { id: 'v2', name: 'Promo Gift Card',   template: 'signature', theme: 'lavender', denominations: [50, 100, 200],     value: 100, promo: 85,   online: true,  pos: true,  sold: 92,  status: 'active', expiry: '12 months', customAmount: false, designIds: [] },
  { id: 'v3', name: 'Starter Voucher',   template: 'signature', theme: 'sunset',   denominations: [25, 40],           value: 25,  promo: null, online: true,  pos: true,  sold: 58,  status: 'active', expiry: 'Never',     customAmount: false, designIds: [] },
  { id: 'v4', name: 'Seasonal Promo',    template: 'signature', theme: 'amber',    denominations: [75, 100],          value: 75,  promo: 60,   online: true,  pos: true,  sold: 31,  status: 'active', expiry: '6 months',  customAmount: false, designIds: [] },
  { id: 'v5', name: 'Escape Package',    template: 'signature', theme: 'plum',     denominations: [150, 250],         value: 150, promo: 120,  online: true,  pos: true,  sold: 28,  status: 'active', expiry: '12 months', customAmount: false, designIds: [] },
  // Corporate Card — curated subset (premium, restrained palette only).
  { id: 'v6', name: 'Corporate Card',    template: 'signature', theme: 'charcoal', denominations: [100, 200, 500],    value: 200, promo: null, online: false, pos: true,  sold: 8,   status: 'active', expiry: '24 months', customAmount: true,  customMin: 100, customMax: 1000, designIds: ['editorial-thankyou', 'editorial-anniversary', 'abstract-anniversary', 'custom-default'] },
  { id: 'v7', name: 'House Signature',   template: 'signature', theme: 'cream',    denominations: [50, 100, 150, 200], value: 100, promo: null, online: true,  pos: true,  sold: 210, status: 'active', expiry: 'Never',     customAmount: true,  customMin: 20, designIds: [] },
  { id: 'v8', name: 'Winter Warm-up',    template: 'signature', theme: 'midnight', denominations: [75, 100],          value: 100, promo: 80,   online: true,  pos: true,  sold: 67,  status: 'active', expiry: '6 months',  customAmount: false, designIds: [] },
  // Summer Glow — seasonal subset (light themes + summery occasions).
  { id: 'v9', name: 'Summer Glow',       template: 'signature', theme: 'coral',    denominations: [50, 75, 100],      value: 75,  promo: null, online: true,  pos: true,  sold: 33,  status: 'draft',  expiry: '12 months', customAmount: false, designIds: ['editorial-justbecause', 'editorial-thankyou', 'abstract-justbecause', 'abstract-blossom'] },
];

// ────────────────────────────────────────────────────────────────────────────
// DESIGNS — the salon's voucher design library.
//
// The salon manages availability here ("Available" toggle = appears in the
// voucher editor's per-voucher design picker). Per-voucher curation happens
// in the editor (phase 2): each voucher.designIds[] cherry-picks from the
// designs marked `available: true` in the library.
//
// Three tiers + Custom:
//   - editorial / abstract: one per occasion (12 each), keyed by OCCASIONS
//   - custom: salon-created, seeded with one demo
// ────────────────────────────────────────────────────────────────────────────

// Build the editorial + abstract entries from OCCASIONS (one per occasion).
// Library label uses `subject` (Cake, Hearts, Rings) instead of `name`
// (Birthday, Valentine's, Anniversary) so the design feels usable for any
// occasion rather than locked to one. See the comment above OCCASIONS.
const editorialDesigns = OCCASIONS.map((o) => {
  const tpl = TEMPLATES.find(t => t.id === o.id) || TEMPLATES[TEMPLATES.length - 1];
  return {
    id: `editorial-${o.id}`,
    aesthetic: 'editorial',
    theme: tpl.suggestedTheme,
    occasion: o.id,
    label: `Editorial · ${o.subject || o.name}`,
    available: true,
    isCustom: false,
  };
});

const abstractDesigns = OCCASIONS.map((o) => {
  const tpl = TEMPLATES.find(t => t.id === o.id) || TEMPLATES[TEMPLATES.length - 1];
  return {
    id: `abstract-${o.id}`,
    aesthetic: 'abstract',
    theme: tpl.suggestedTheme,
    occasion: o.id,
    label: `Abstract · ${o.paletteLabel || o.subject || o.name}`,
    available: true,
    isCustom: false,
  };
});

// ────────────────────────────────────────────────────────────────────────────
// Custom design config — written by the salon's Custom Designer slide-out.
//
// Schema is deliberately rich so the editor can offer genuine layout choice.
// All fields have sensible defaults — defaultCustomConfig() returns a fresh
// config object for new customs. Older customs (with thinner shapes) are
// upgraded on read via the same defaults via spread, so the editor never
// crashes on missing fields.
// ────────────────────────────────────────────────────────────────────────────

export const CUSTOM_LAYOUTS = [
  { id: 'editorial', name: 'Editorial', desc: 'Brand mark top, oversized amount, tagline bottom' },
  { id: 'hero',      name: 'Hero',      desc: 'Logo centered, amount below as the hero' },
  { id: 'minimal',   name: 'Minimal',   desc: 'Pure type, no chrome' },
  { id: 'mark',      name: 'Mark',      desc: 'Just the logo, amount overlaid' },
  { id: 'stack',     name: 'Stack',     desc: 'Photo / colour dominant, text at the bottom' },
];

export const LOGO_POSITIONS = ['tl','tc','tr','center','bc'];
export const LOGO_SIZES     = ['sm','md','lg'];

// Font picker — 6 curated families. Loaded together via the single
// Google Fonts @import at the top of GiftCardsV2.css so the picker
// can render each option in its actual face. The `family` string is
// applied inline to the card root by CustomCardScene.
export const FONT_OPTIONS = [
  { id: 'fraunces',  label: 'Fraunces',  family: "'Fraunces', Georgia, serif" },
  { id: 'playfair',  label: 'Playfair',  family: "'Playfair Display', Georgia, serif" },
  { id: 'dmserif',   label: 'DM Serif',  family: "'DM Serif Display', Georgia, serif" },
  { id: 'cormorant', label: 'Cormorant', family: "'Cormorant Garamond', Georgia, serif" },
  { id: 'inter',     label: 'Inter',     family: "'Inter', -apple-system, sans-serif" },
  { id: 'system',    label: 'System',    family: "-apple-system, BlinkMacSystemFont, 'Helvetica Neue', sans-serif" },
];
// Legacy alias map — older customs stored `fontFamily: 'serif' | 'sans'`.
const LEGACY_FONT_MAP = { serif: 'fraunces', sans: 'system' };

// A curated palette of brand colours pulled from THEMES, plus
// utility black/white. Shown as swatches in the colour pickers.
export const BRAND_SWATCHES = [
  '#29282D', // charcoal
  '#1A1A1E', // noir
  '#FFFFFF', // white
  '#F3EADD', // cream
  '#E8B4C8', // rose
  '#F0654B', // coral
  '#EC8C55', // sunset
  '#E8A838', // amber
  '#0D3B4F', // ocean
  '#007EA0', // teal
  '#8DB580', // sage
  '#3C1F3A', // plum
];

// Mint a stable element id for a new element in the canvas.
export const mintElementId = () =>
  `el-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 5)}`;

export const defaultCustomConfig = () => ({
  // Background
  bgMode: 'solid',          // 'solid' | 'gradient' | 'photo' | 'pattern'
  bgColor: '#29282D',
  bgGradient: { from: '#29282D', to: '#0D3B4F', angle: 135 },
  bgPhoto: null,            // { url, focalX, focalY, zoom }
  bgPattern: { occasion: 'birthday' },
  ink: '#FFFFFF',
  accent: '#F5DABF',

  fontFamily: 'fraunces',   // default font for newly-added text elements

  // The card content. Each element renders as a positioned overlay
  // (drag, resize, right-click, etc.). Array order = stacking order.
  //
  //   { id, type: 'amount', x, y, scale, font, color, fontSize }
  //   { id, type: 'text',   x, y, scale, content, font, color,
  //                          bold, italic, underline, align, fontSize }
  //   { id, type: 'image',  x, y, scale, src }
  //   { id, type: 'shape',  x, y, scale, shape, fill, stroke, strokeWidth, radius }
//     radius (0-100) = corner roundness; see ShapeRenderer.
  elements: [
    { id: 'el-amount-default', type: 'amount', x: 28, y: 52, scale: 1, font: 'fraunces', color: '#FFFFFF', fontSize: 48 },
  ],
});

// Default text styling for newly-added text elements. Centralised so
// the rail's "Add text" action and the Heading / Subheading / Body
// presets stay consistent.
export const TEXT_DEFAULTS = {
  bold: false,
  italic: false,
  underline: false,
  align: 'center',
  fontSize: 22,
};

// Text presets — three styled starting points that drop styled text
// elements onto the canvas in one click.
export const TEXT_PRESETS = [
  { id: 'heading',    label: 'Heading',    fontSize: 36, bold: true,  italic: false },
  { id: 'subheading', label: 'Subheading', fontSize: 22, bold: false, italic: false },
  { id: 'body',       label: 'Body',       fontSize: 14, bold: false, italic: false },
];

// Shape primitives surfaced in the Shapes panel.
export const SHAPE_TYPES = [
  { id: 'rect',     label: 'Rectangle' },
  { id: 'circle',   label: 'Circle' },
  { id: 'triangle', label: 'Triangle' },
  { id: 'diamond',  label: 'Diamond' },
  { id: 'pentagon', label: 'Pentagon' },
  { id: 'hexagon',  label: 'Hexagon' },
  { id: 'star',     label: 'Star' },
  { id: 'heart',    label: 'Heart' },
  { id: 'arrow',    label: 'Arrow' },
  { id: 'line',     label: 'Line' },
];

// Upgrade a (possibly thin) older config to the full shape via defaults.
// Used by CustomCardScene and the designer so they don't have to special-case
// missing fields.
export const normaliseCustomConfig = (config) => {
  const c = { ...defaultCustomConfig(), ...(config || {}) };
  // Legacy `bg` field → bgColor
  if (config?.bg && !config?.bgColor) c.bgColor = config.bg;
  // Legacy fontFamily ('serif' | 'sans') → new ids
  if (LEGACY_FONT_MAP[c.fontFamily]) c.fontFamily = LEGACY_FONT_MAP[c.fontFamily];

  // Migrate legacy named slots (logoImage / logoText / markImage /
  // headline + amountShow) into the elements[] array. Done once at
  // read time so old saved customs still render after the schema
  // change, without forcing a write-through migration.
  if (!config?.elements && config) {
    const elements = [];

    // Logo — image preferred over text wordmark
    if (config.logoImage?.url) {
      elements.push({
        id: 'legacy-logo',
        type: 'image',
        src: config.logoImage.url,
        x: config.logoX ?? 50,
        y: config.logoY ?? 18,
        scale: config.logoScale ?? 1,
      });
    } else if (config.logoText) {
      elements.push({
        id: 'legacy-logo-text',
        type: 'text',
        content: config.logoText,
        x: config.logoX ?? 50,
        y: config.logoY ?? 18,
        scale: config.logoScale ?? 1,
        font: c.fontFamily,
      });
    }

    // Brand mark — same shape as image element, just a second one
    if (config.markImage?.url) {
      elements.push({
        id: 'legacy-mark',
        type: 'image',
        src: config.markImage.url,
        x: config.markX ?? 88,
        y: config.markY ?? 88,
        scale: config.markScale ?? 1,
      });
    }

    // Amount — kept by default; explicit amountShow:false hides it
    if (config.amountShow !== false) {
      elements.push({
        id: 'legacy-amount',
        type: 'amount',
        x: config.amountX ?? 28,
        y: config.amountY ?? 52,
        scale: config.amountScale ?? 1,
        font: c.fontFamily,
      });
    }

    // Headline → text element
    if (config.headline) {
      elements.push({
        id: 'legacy-headline',
        type: 'text',
        content: config.headline,
        x: config.headlineX ?? 50,
        y: config.headlineY ?? 84,
        scale: config.headlineScale ?? 1,
        font: c.fontFamily,
      });
    }

    if (elements.length) c.elements = elements;
  }

  // Ensure elements is always an array
  if (!Array.isArray(c.elements)) c.elements = [];

  return c;
};

const customDesigns = [
  {
    id: 'custom-default',
    aesthetic: 'custom',
    theme: 'charcoal',
    occasion: null,
    label: 'Salon brand',
    available: true,
    isCustom: true,
    config: {
      ...defaultCustomConfig(),
      logoText: 'beu',
      headline: 'A treat from us',
      bgColor: '#29282D',
      ink: '#FFFFFF',
      accent: '#F5DABF',
    },
  },
];

export const DESIGNS = [
  ...editorialDesigns,
  ...abstractDesigns,
  ...customDesigns,
];

// Library helpers.
export const getDesignsByAesthetic = (designs, aesthetic) =>
  designs.filter(d => d.aesthetic === aesthetic);

export const countAvailable = (designs) =>
  designs.filter(d => d.available).length;

export const getTpl     = (id) => TEMPLATES.find(t => t.id === id) || TEMPLATES[TEMPLATES.length - 1];
export const getTheme   = (id) => THEMES.find(t => t.id === id)    || THEMES[1];
export const getOccasion = (id) => OCCASIONS.find(o => o.id === id) || OCCASIONS[0];
export const getDesign  = (designs, id) => designs.find(d => d.id === id);

// ────────────────────────────────────────────────────────────────────────────
// Voucher ↔ design cross-reference.
//
// A voucher "uses" a design when:
//   - the design is available in the library, AND
//   - the voucher's designIds[] is empty (= offer every available design)
//     OR the voucher's designIds[] includes this design's id.
//
// Used by the design slide-out's "Used by" section.
// ────────────────────────────────────────────────────────────────────────────
export const getVouchersUsingDesign = (vouchers, design) => {
  if (!design || !design.available) return [];
  return vouchers.filter((v) => {
    const ids = v.designIds || [];
    return ids.length === 0 || ids.includes(design.id);
  });
};

// ────────────────────────────────────────────────────────────────────────────
// Mock design stats — deterministic from the design id so the same design
// always shows the same numbers. Real values come from the backend in
// phase 2; this just lets the slide-out demonstrate the shape of the data.
//
// Returns: picked count, shown count (impressions), pick rate %, days
// since last pick, % trend vs prior period, and the per-aesthetic rank
// (computed by callers from a sorted list).
// ────────────────────────────────────────────────────────────────────────────
const hashStr = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};

export const mockDesignStats = (designId) => {
  const h = hashStr(designId);
  const picked = (h % 220) + 3;                  // 3..222 picks
  const pickRatePct = 30 + (h % 30);             // 30%..59% pick rate
  const shown = Math.round(picked / (pickRatePct / 100));
  const daysSincePick = (h % 30) + 1;            // 1..30 days ago
  const trendPct = ((h % 50) - 18);              // -18%..+31%
  return { picked, shown, pickRatePct, daysSincePick, trendPct };
};

// Per-aesthetic rank — call once per aesthetic and look up by design id.
export const rankByPickedInAesthetic = (designs, aesthetic) => {
  const ranked = designs
    .filter(d => d.aesthetic === aesthetic && d.available)
    .map(d => ({ id: d.id, picked: mockDesignStats(d.id).picked }))
    .sort((a, b) => b.picked - a.picked);
  const map = {};
  ranked.forEach((r, i) => { map[r.id] = i + 1; });
  return map;
};

// ────────────────────────────────────────────────────────────────────────────
// Voucher sales (mock data) — phase 1 has no backend, so the Sales page
// reads from a deterministic in-memory list seeded proportionally to each
// voucher's existing `sold: N` count. This keeps the Vouchers table's
// `Sold` column consistent with what the Sales table displays.
//
// Distribution rules (so the page looks alive, not toy):
//   - Spread across the last 12 months, weighted by month (Dec + Feb
//     spikes for Christmas / Valentine's, May bump for Mother's Day)
//   - 60 / 40 online / POS split, except for vouchers that explicitly
//     disable a channel (e.g. v6 Corporate Card is POS-only)
//   - Status skews by age: recent = mostly active, older = more redeemed
//     / partial / expired so the redemption-rate KPI lands plausibly
//   - ~10% of POS sales have null buyer + recipient (the cash walk-out
//     case) so the Unclaimed workflows have real data to render against
//   - ~60% of sales are gifts (distinct recipient), the rest are
//     buyer-as-recipient
// ────────────────────────────────────────────────────────────────────────────

const SALES_FIRST_NAMES = [
  'Sarah', 'Emma', 'Oliver', 'James', 'Charlotte', 'Harry', 'Amelia', 'George',
  'Ava', 'Jack', 'Isla', 'Leo', 'Mia', 'Noah', 'Olivia', 'William', 'Sophie',
  'Henry', 'Grace', 'Thomas', 'Lily', 'Theo', 'Freya', 'Arthur', 'Daisy',
];
const SALES_LAST_INITS = ['K', 'M', 'T', 'R', 'C', 'P', 'B', 'H', 'L', 'S', 'D', 'W', 'F', 'A'];
const SALES_DOMAINS = ['gmail.com', 'outlook.com', 'icloud.com', 'hotmail.com', 'yahoo.co.uk'];
const SALES_MESSAGES = [
  'Happy birthday!', 'With love', 'For you', 'Treat yourself', 'Enjoy x',
  'Thinking of you', 'A little something', 'Hope you love it', 'For all your hard work',
  null, null, null,  // ~25% of gift sales have no message
];

// Deterministic pseudo-random — same seed always yields the same value,
// so the mock data is stable across re-renders and across reloads.
const seededFloat = (seed) => (hashStr(String(seed)) % 100000) / 100000;
const seededInt = (seed, max) => Math.floor(seededFloat(seed) * max);
const seededPick = (seed, arr) => arr[seededInt(seed, arr.length)];

// Voucher code format: BEU-XXXX-XXXX-XXXX where X is from a legible
// charset (ambiguous I, O, 0, 1 dropped so staff can read codes out
// over the phone without confusion).
const VOUCHER_CODE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'.split('');
const mintVoucherCode = (seed) => {
  const groups = [];
  for (let g = 0; g < 3; g++) {
    let s = '';
    for (let i = 0; i < 4; i++) {
      s += seededPick(`${seed}-c-${g}-${i}`, VOUCHER_CODE_CHARS);
    }
    groups.push(s);
  }
  return `BEU-${groups.join('-')}`;
};

// Monthly demand weights (1.0 = average).
const monthWeight = (m) => {
  if (m === 11) return 2.2;  // Dec — Christmas spike
  if (m === 1)  return 1.9;  // Feb — Valentine's
  if (m === 4)  return 1.3;  // May — Mother's Day
  if (m === 9)  return 0.85; // October dip
  return 1.0;
};

// Pick a soldAt timestamp in the last 12 months, weighted by month.
// Rejection sampling — try uniform candidates, accept with prob = weight/max.
const pickSoldAtMs = (seed, nowMs) => {
  const YEAR = 365 * 86400000;
  for (let t = 0; t < 8; t++) {
    const u = seededFloat(`${seed}-when-${t}`);
    const ms = nowMs - u * YEAR;
    const w = monthWeight(new Date(ms).getMonth());
    if (seededFloat(`${seed}-accept-${t}`) < w / 2.2) return ms;
  }
  return nowMs - seededFloat(`${seed}-fb`) * YEAR;
};

// Status distribution shifts with age.
const pickStatus = (seed, ageMonths) => {
  const r = seededFloat(`${seed}-status`);
  if (ageMonths < 2) {
    if (r < 0.95) return 'active';
    if (r < 0.99) return 'redeemed';
    return 'partial';
  }
  if (ageMonths < 6) {
    if (r < 0.55) return 'active';
    if (r < 0.85) return 'redeemed';
    if (r < 0.95) return 'partial';
    return 'expired';
  }
  if (r < 0.25) return 'active';
  if (r < 0.70) return 'redeemed';
  if (r < 0.85) return 'partial';
  return 'expired';
};

const buildSale = (saleId, voucher, designs, nowMs) => {
  const code = mintVoucherCode(saleId);
  const soldAtMs = pickSoldAtMs(saleId, nowMs);
  const soldAt = new Date(soldAtMs);
  const ageMonths = (nowMs - soldAtMs) / (30 * 86400000);
  const status = pickStatus(saleId, ageMonths);

  // Channel — honour each voucher's online/pos flags.
  const onlineOK = voucher.online !== false;
  const posOK = voucher.pos !== false;
  let channel;
  if (onlineOK && posOK) channel = seededFloat(`${saleId}-chan`) < 0.6 ? 'online' : 'pos';
  else channel = onlineOK ? 'online' : 'pos';

  // Face value picked from voucher.denominations.
  const faceValue = seededPick(`${saleId}-fv`, voucher.denominations);

  // Promo: if voucher.promo is set and < value, scale paid amount.
  const promoActive = voucher.promo != null && voucher.promo < voucher.value;
  const paidAmount = promoActive
    ? Math.round(faceValue * (voucher.promo / voucher.value))
    : faceValue;

  // Redeemed amount mirrors status.
  let redeemedAmount = 0;
  if (status === 'redeemed') redeemedAmount = faceValue;
  else if (status === 'partial') redeemedAmount = Math.max(1, Math.floor(faceValue * (0.2 + seededFloat(`${saleId}-r`) * 0.6)));

  // Expiry — read from voucher.expiry ("12 months", "Never", etc.).
  const expiryMonthsMatch = (voucher.expiry || '').match(/(\d+)/);
  const expiryMonths = expiryMonthsMatch ? parseInt(expiryMonthsMatch[1], 10) : 12;
  let expiresAtMs = soldAtMs + expiryMonths * 30 * 86400000;
  if (status === 'expired' && expiresAtMs > nowMs) {
    // Force a past expiry so the data is internally consistent.
    expiresAtMs = nowMs - (1 + seededInt(`${saleId}-exp`, 14)) * 86400000;
  }

  // Unclaimed: ~10% of POS sales. Leaves buyer + recipient nulled so
  // the "cash walk-out, no email captured" workflow has real records.
  const unclaimed = channel === 'pos' && seededFloat(`${saleId}-unclm`) < 0.1;

  let buyerName = null;
  let buyerEmail = null;
  let recipientName = null;
  let recipientEmail = null;
  let message = null;
  if (!unclaimed) {
    const bf = seededPick(`${saleId}-bf`, SALES_FIRST_NAMES);
    const bi = seededPick(`${saleId}-bi`, SALES_LAST_INITS);
    buyerName = `${bf} ${bi}.`;
    buyerEmail = `${bf.toLowerCase()}.${bi.toLowerCase()}@${seededPick(`${saleId}-bd`, SALES_DOMAINS)}`;

    if (seededFloat(`${saleId}-gift`) < 0.6) {
      const rf = seededPick(`${saleId}-rf`, SALES_FIRST_NAMES);
      const ri = seededPick(`${saleId}-ri`, SALES_LAST_INITS);
      recipientName = `${rf} ${ri}.`;
      recipientEmail = `${rf.toLowerCase()}.${ri.toLowerCase()}@${seededPick(`${saleId}-rd`, SALES_DOMAINS)}`;
      message = seededPick(`${saleId}-msg`, SALES_MESSAGES);
    }
  }

  // Pick a design id — prefer one from voucher.designIds, otherwise fall
  // back to an editorial design that matches the voucher's template.
  let designId;
  if (voucher.designIds && voucher.designIds.length) {
    designId = seededPick(`${saleId}-design`, voucher.designIds);
  } else {
    const def = designs.find(d => d.aesthetic === 'editorial' && d.occasion === voucher.template);
    designId = def ? def.id : 'editorial-birthday';
  }

  return {
    id: saleId,
    code,
    voucherId: voucher.id,
    designId,
    faceValue,
    paidAmount,
    buyerName,
    buyerEmail,
    recipientName,
    recipientEmail,
    message,
    channel,
    soldAt: soldAt.toISOString(),
    expiresAt: new Date(expiresAtMs).toISOString(),
    redeemedAmount,
    status,
  };
};

// Build once at module load (deterministic, so no per-render cost).
const NOW_MS = Date.now();
export const SAMPLE_VOUCHER_SALES = (() => {
  const all = [];
  SAMPLE_VOUCHERS.forEach((v) => {
    for (let i = 0; i < (v.sold || 0); i++) {
      all.push(buildSale(`${v.id}-${i}`, v, DESIGNS, NOW_MS));
    }
  });
  // Most recent first — the Sales table opens to this order by default.
  return all.sort((a, b) => new Date(b.soldAt) - new Date(a.soldAt));
})();

// ─── Sales helpers ───────────────────────────────────────────────────────

export const isSaleUnclaimed = (sale) =>
  !!sale && !sale.buyerEmail && !sale.buyerName;

// Filter sales by date range, channel, voucher, unclaimed flag, free-text
// query. `query` matches against code + recipient + buyer fields so the
// salon's search bar covers every lookup path.
export const filterSales = (sales, opts = {}) => {
  const { from, to, channel, voucherId, unclaimed, query } = opts;
  const fromMs = from ? new Date(from).getTime() : null;
  const toMs = to ? new Date(to).getTime() : null;
  const q = (query || '').trim().toLowerCase();
  return sales.filter((s) => {
    const t = new Date(s.soldAt).getTime();
    if (fromMs != null && t < fromMs) return false;
    if (toMs != null && t > toMs) return false;
    if (channel && channel !== 'all' && s.channel !== channel) return false;
    if (voucherId && s.voucherId !== voucherId) return false;
    if (unclaimed && !isSaleUnclaimed(s)) return false;
    if (q) {
      const hay = [s.code, s.recipientName, s.recipientEmail, s.buyerName, s.buyerEmail]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
};

// Bucket sales for the time-series chart. Returns ordered array of
// `{ key, label, total, count, online, pos, sampleDate }` rows ready for
// Recharts; total + count tracked separately so the chart can toggle.
export const aggregateOverTime = (sales, granularity = 'daily') => {
  if (!sales.length) return [];
  const bucketKey = (d) => {
    if (granularity === 'monthly') return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (granularity === 'weekly') {
      // ISO-week start (Monday)
      const day = (d.getDay() + 6) % 7;
      const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - day);
      return monday.toISOString().slice(0, 10);
    }
    return d.toISOString().slice(0, 10);
  };

  const buckets = new Map();
  sales.forEach((s) => {
    const d = new Date(s.soldAt);
    const k = bucketKey(d);
    if (!buckets.has(k)) {
      buckets.set(k, { key: k, total: 0, count: 0, online: 0, pos: 0, sampleDate: d });
    }
    const b = buckets.get(k);
    b.total += s.paidAmount;
    b.count += 1;
    b[s.channel] += s.paidAmount;
  });

  return Array.from(buckets.values())
    .sort((a, b) => a.sampleDate - b.sampleDate)
    .map((b) => {
      let label;
      if (granularity === 'monthly') label = b.sampleDate.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
      else label = b.sampleDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
      return { ...b, label };
    });
};

// KPI rollup. When `prevSales` is given, change badges are computed as
// percent-deltas (or absolute %-point delta for the redemption rate,
// which is itself a percentage).
export const computeKpis = (sales, prevSales = null) => {
  const sum = (arr, key) => arr.reduce((s, x) => s + (x[key] || 0), 0);
  const outstandingOf = (arr) => arr.reduce((s, x) => {
    if (x.status === 'active' || x.status === 'partial') return s + (x.faceValue - x.redeemedAmount);
    return s;
  }, 0);

  const count = sales.length;
  const revenue = sum(sales, 'paidAmount');
  const outstanding = outstandingOf(sales);
  const totalFace = sum(sales, 'faceValue');
  const totalRedeemed = sum(sales, 'redeemedAmount');
  const redemptionRate = totalFace > 0 ? Math.round((totalRedeemed / totalFace) * 100) : 0;

  if (!prevSales) return { count, revenue, outstanding, redemptionRate };

  const prevCount = prevSales.length;
  const prevRevenue = sum(prevSales, 'paidAmount');
  const prevOutstanding = outstandingOf(prevSales);
  const prevFace = sum(prevSales, 'faceValue');
  const prevRedeemed = sum(prevSales, 'redeemedAmount');
  const prevRate = prevFace > 0 ? Math.round((prevRedeemed / prevFace) * 100) : 0;

  const pct = (cur, prev) => {
    if (prev === 0) return cur === 0 ? 0 : 100;
    return Math.round(((cur - prev) / prev) * 100);
  };

  return {
    count,
    revenue,
    outstanding,
    redemptionRate,
    countChange: pct(count, prevCount),
    revenueChange: pct(revenue, prevRevenue),
    outstandingChange: pct(outstanding, prevOutstanding),
    redemptionRateChange: redemptionRate - prevRate,  // absolute %-point delta
  };
};

// Top-selling voucher PRODUCTS by revenue. Aggregates sales by voucherId
// (the product the salon sells, e.g. "Gift Card", "House Signature") and
// joins to the voucher record for its name. This is more actionable for
// the salon than ranking by design skin — they manage and price vouchers,
// not designs.
export const getTopVouchersByRevenue = (sales, vouchers, n = 5) => {
  const byVoucher = new Map();
  sales.forEach((s) => {
    if (!byVoucher.has(s.voucherId)) byVoucher.set(s.voucherId, { voucherId: s.voucherId, count: 0, revenue: 0 });
    const e = byVoucher.get(s.voucherId);
    e.count += 1;
    e.revenue += s.paidAmount;
  });
  return Array.from(byVoucher.values())
    .map((e) => ({ ...e, voucher: vouchers.find(v => v.id === e.voucherId) || null }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, n);
};

export const getChannelBreakdown = (sales) => {
  const online = sales.filter(s => s.channel === 'online');
  const pos = sales.filter(s => s.channel === 'pos');
  const onlineRevenue = online.reduce((s, x) => s + x.paidAmount, 0);
  const posRevenue = pos.reduce((s, x) => s + x.paidAmount, 0);
  const total = onlineRevenue + posRevenue;
  return {
    online: {
      count: online.length,
      revenue: onlineRevenue,
      percent: total > 0 ? Math.round((onlineRevenue / total) * 100) : 0,
    },
    pos: {
      count: pos.length,
      revenue: posRevenue,
      percent: total > 0 ? Math.round((posRevenue / total) * 100) : 0,
    },
    total,
  };
};

// Active or partial sales whose expiry falls within the next N days.
export const getExpiringSoon = (sales, withinDays = 30) => {
  const now = Date.now();
  const limit = now + withinDays * 86400000;
  return sales.filter((s) => {
    if (s.status !== 'active' && s.status !== 'partial') return false;
    const expMs = new Date(s.expiresAt).getTime();
    return expMs > now && expMs <= limit;
  });
};

export const getUnclaimed = (sales) => sales.filter(isSaleUnclaimed);
